import React, { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  OTPCodeReqBody,
  PostLoginRes,
} from '../../apis/kstadiumAPI/logIn/types';

import KStadiumAPI from '../../apis/kstadiumAPI';
import {
  removeDisposableTokenTokenInLocalStorage,
  setDisposableTokenTokenInLocalStorage,
  setTokenInLocalStorage,
} from '../../storage/token';
import { isKStadiumServerErrorRes } from '../../utils/logIn';

export type OTPType = `logIn` | `confirm`;

export type OTPModalType = `otp` | `failed` | `timeLimit`;

const useOTP = (
  type: OTPType,
  setOTPModal: React.Dispatch<React.SetStateAction<OTPModalType | null>>,
  onSuccess: (result: { accessToken: string; refreshToken: string }) => void,
) => {
  const { control, handleSubmit, reset, setValue } = useForm<OTPCodeReqBody>({
    mode: `onChange`,
    defaultValues: {
      otpCode: ``,
    },
  });
  const { mutateAsync: postOTP } = useMutation<
    PostLoginRes,
    AxiosError,
    OTPCodeReqBody
  >(type === `logIn` ? KStadiumAPI.logInOTP : KStadiumAPI.confirmOTP, {
    onSuccess: ({ data: tokens }) => {
      if (type === `logIn`) {
        removeDisposableTokenTokenInLocalStorage();
        setTokenInLocalStorage(tokens);
      } else {
        setDisposableTokenTokenInLocalStorage(tokens.accessToken);
      }

      onSuccess(tokens);
      setOTPModal(null);
    },
    onError: (error) => {
      reset();

      const data = error.response?.data;

      if (isKStadiumServerErrorRes(data)) {
        switch (data.statusCode) {
          case `KS4031`:
            setOTPModal(`failed`);
            break;
          case `KS4013`:
            setOTPModal(`timeLimit`);
            break;
          default:
            setOTPModal(null);
        }
      }
    },
  });

  const onSubmit: SubmitHandler<OTPCodeReqBody> = useCallback(
    async (data) => {
      await postOTP(data);
    },
    [postOTP],
  );

  return {
    control,
    onSubmit: handleSubmit(onSubmit),
    reset,
  };
};

export default useOTP;
